import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Users, Trophy, Plane, Star, Zap, Crown, TrendingUp, Award, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import ContentGate from '../components/ContentGate';
import AuthModal from '../components/AuthModal';
import { LeaderboardEntry, BadgeShowcase, RankRoadmap, XPRing, ALL_RANKS, getXpProgress } from '../components/GamificationUI';

import { API } from '../config';

const RANK_CONFIG = {
  'Boarding Pass':  { icon: '🎫', color: '#6B7280', bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-200' },
  'Frequent Flyer': { icon: '✈️', color: '#1E3A8A', bg: 'bg-[#1E3A8A]/10', border: 'border-[#1E3A8A]/30' },
  'Silver Traveler':{ icon: '🥈', color: '#94A3B8', bg: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-300' },
  'Gold Traveler':  { icon: '🥇', color: '#D97706', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200' },
  'Platinum Flyer': { icon: '💎', color: '#7C3AED', bg: 'bg-violet-50 dark:bg-violet-900/20', border: 'border-violet-200' },
  'Sky Legend':     { icon: '🌟', color: '#FF4500', bg: 'bg-[#FF4500]/10', border: 'border-[#FF4500]/30' },
};

const BADGES_INFO = [
  { id: 'New Traveler', icon: '🌱', label: 'New Traveler', desc: 'Welcome to Fly N Out!', xp: 0, color: '#10B981' },
  { id: 'First Flight', icon: '🛫', label: 'First Flight', desc: 'Log your first flight', xp: 10, color: '#1E3A8A' },
  { id: 'Regular Flyer', icon: '✈️', label: 'Regular Flyer', desc: 'Log 5 flights', xp: 50, color: '#FF4500' },
  { id: 'Frequent Flyer', icon: '🏆', label: 'Frequent Flyer', desc: 'Log 10 flights', xp: 100, color: '#D97706' },
  { id: 'Jet Setter', icon: '🌍', label: 'Jet Setter', desc: 'Log 25 flights', xp: 250, color: '#7C3AED' },
  { id: 'Sky High', icon: '🚀', label: 'Sky High', desc: 'Log 50 flights', xp: 500, color: '#FF4500' },
  { id: 'Community Star', icon: '⭐', label: 'Community Star', desc: 'Post 20 experiences', xp: 100, color: '#F59E0B' },
  { id: 'Layover Legend', icon: '⏳', label: 'Layover Legend', desc: 'Find 5 layover buddies', xp: 50, color: '#10B981' },
  { id: 'Gate Guru', icon: '🚪', label: 'Gate Guru', desc: 'Share 10 helpful airport tips', xp: 100, color: '#1E3A8A' },
  { id: 'Fly N Out Club Member', icon: '👑', label: 'Club Member', desc: 'Subscribe to Fly N Out Club', xp: 200, color: '#FF4500' },
  { id: 'Explorer', icon: '🗺️', label: 'Explorer', desc: 'Visit 10 different airports', xp: 100, color: '#10B981' },
  { id: 'Verified Traveler', icon: '✅', label: 'Verified', desc: 'Complete profile verification', xp: 50, color: '#1E3A8A' },
];

const RANKS_PATH = [
  { name: 'Boarding Pass', icon: '🎫', xp: 0, desc: 'You just got your boarding pass. Welcome aboard!' },
  { name: 'Frequent Flyer', icon: '✈️', xp: 50, desc: 'You fly enough to know the best seats and lounges.' },
  { name: 'Silver Traveler', icon: '🥈', xp: 150, desc: 'A seasoned traveler with a story to tell at every airport.' },
  { name: 'Gold Traveler', icon: '🥇', xp: 300, desc: 'You\'ve mastered the art of travel and help others do the same.' },
  { name: 'Platinum Flyer', icon: '💎', xp: 600, desc: 'An elite traveler known across every terminal.' },
  { name: 'Sky Legend', icon: '🌟', xp: 1000, desc: 'The pinnacle of the Fly N Out community. A true sky legend.' },
];

function XPBar({ current, next, rank }) {
  const progress = next ? Math.min(100, (current / next.xp_needed) * 100) : 100;
  const cfg = RANK_CONFIG[rank?.name] || RANK_CONFIG['Boarding Pass'];
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1 text-xs">
        <span className="font-medium">{rank?.icon} {rank?.name}</span>
        {next && <span className="text-muted-foreground">{current} / {next.xp_needed} XP → {next.name}</span>}
        {!next && <span className="text-[#FF4500] font-medium">Max rank achieved! 🌟</span>}
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: cfg.color }}
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('xp');
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lbRes, statsRes] = await Promise.all([
          axios.get(`${API}/leaderboard?limit=20&category=${category}`),
          axios.get(`${API}/community/stats`),
        ]);
        setLeaderboard(lbRes.data);
        setStats(statsRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [category]);

  const userRank = user?.rank || user?.rank_info;
  const cfg = userRank ? (RANK_CONFIG[userRank?.name || userRank] || RANK_CONFIG['Boarding Pass']) : null;

  return (
    <PageLayout>
      <SEOHead
        title="Community Leaderboard & Badges — Fly N Out"
        description="Earn XP, collect badges, and climb the Fly N Out leaderboard. Track your rank from Boarding Pass to Sky Legend and compete with travelers worldwide."
        url="/community"
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#111827] to-[#1E3A8A]/80 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex justify-center mb-3">
            <div className="w-14 h-14 rounded-2xl bg-[#FF4500]/20 flex items-center justify-center">
              <Trophy className="w-7 h-7 text-[#FF4500]" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Community & Rankings</h1>
          <p className="text-gray-300 mb-6">Earn XP, unlock badges, and climb the leaderboard</p>
          {stats && (
            <div className="flex justify-center flex-wrap gap-6">
              {[
                [stats.total_users, 'Travelers', Users],
                [stats.total_flights_logged, 'Flights Logged', Plane],
                [stats.total_posts, 'Experiences Shared', Star],
                [stats.total_routes, 'Routes Explored', TrendingUp],
              ].map(([val, label, Icon]) => (
                <div key={label} className="text-center">
                  <div className="text-2xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{val?.toLocaleString()}</div>
                  <div className="text-xs text-gray-400 flex items-center gap-1 justify-center"><Icon className="w-3 h-3" />{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* My Rank Card */}
        {user && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Card className="fly-card p-5 overflow-hidden relative">
              {/* Background accent */}
              <div
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at 80% 50%, ${(ALL_RANKS.find(r => r.name === (user.rank_info?.name || user.rank)) || ALL_RANKS[0]).color}, transparent)`,
                }}
              />
              <div className="relative flex items-center gap-4">
                {/* XP Ring + Avatar */}
                <div className="relative flex-shrink-0">
                  <XPRing xp={user.xp || 0} size={72} strokeWidth={7} />
                  <img
                    src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.user_id}`}
                    alt=""
                    className="absolute inset-0 m-auto w-10 h-10 rounded-full object-cover"
                    style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', marginTop: 0, marginLeft: 0 }}
                    onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.user_id}`; }}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-bold" style={{ fontFamily: 'Space Grotesk' }}>{user.display_name || user.name?.split(' ')[0]}</h2>
                    {user.is_premium && <Crown className="w-4 h-4 text-[#FF4500]" />}
                  </div>
                  <RankRoadmap xp={user.xp || 0} compact />
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-2xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{user.xp || 0}</div>
                  <div className="text-xs text-muted-foreground">Total XP</div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        <Tabs defaultValue="leaderboard">
          <TabsList className="mb-6">
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="badges">All Badges</TabsTrigger>
            <TabsTrigger value="ranks">Rank Path</TabsTrigger>
          </TabsList>

          {/* LEADERBOARD */}
          <TabsContent value="leaderboard">
            <div className="flex gap-2 mb-4">
              {[['xp', 'Top XP', Zap], ['flights', 'Top Flyers', Plane]].map(([val, label, Icon]) => (
                <button key={val} onClick={() => setCategory(val)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${category === val ? 'bg-[#FF4500] text-white' : 'bg-muted hover:bg-muted/80'}`}>
                  <Icon className="w-3.5 h-3.5" />{label}
                </button>
              ))}
            </div>
            {loading ? (
              <div className="space-y-2">{[...Array(10)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
            ) : (
              <ContentGate
                user={user}
                visibleCount={5}
                title="Join to see the full leaderboard"
                message="Create a free account to see all rankings, earn XP, and climb the Fly N Out leaderboard."
                onSignUp={() => setShowAuthModal(true)}
              >
                {leaderboard.map((member, i) => (
                  <LeaderboardEntry
                    key={member.user_id}
                    member={member}
                    position={i}
                    isCurrentUser={user && member.user_id === user.user_id}
                    category={category}
                  />
                ))}
              </ContentGate>
            )}
          </TabsContent>

          {/* BADGES */}
          <TabsContent value="badges">
            <p className="text-sm text-muted-foreground mb-5">
              Earn badges by completing challenges on Fly N Out.
              {user ? ` You've earned ${user.badges?.length || 0} of ${12} badges.` : ' Log in to track your progress.'}
            </p>
            <BadgeShowcase earnedBadges={user?.badges || []} showLocked={true} />
          </TabsContent>

          {/* RANKS */}
          <TabsContent value="ranks">
            <p className="text-sm text-muted-foreground mb-6">
              The Fly N Out ranking system rewards active community members.
              Earn XP by logging flights, sharing experiences, and helping fellow travelers.
            </p>
            <RankRoadmap xp={user?.xp || 0} />
            <Card className="fly-card p-4 mt-6 bg-muted/30">
              <h3 className="font-semibold mb-3 text-sm" style={{ fontFamily: 'Space Grotesk' }}>How to earn XP</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  ['Log a flight', '+10 XP'],
                  ['Share a route experience', '+5 XP'],
                  ['Post an airport update', '+5 XP'],
                  ['Get a helpful reaction', '+2 XP'],
                  ['Find a layover buddy', '+8 XP'],
                  ['Earn a badge', '+varies'],
                ].map(([action, xp]) => (
                  <div key={action} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0">
                    <span className="text-muted-foreground">{action}</span>
                    <span className="font-semibold text-[#FF4500]">{xp}</span>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </PageLayout>
  );
}
